import { apiFetch } from "../utils/apiFetch";

/**
 * Rechtschreib- und Grammatikprüfung (T-243) über das KMVS-Backend, das an den
 * selbst gehosteten LanguageTool-Container weiterreicht. Der Text verlässt den
 * Server nicht und wird dort weder gespeichert noch geloggt.
 */

export type RechtschreibArt = "rechtschreibung" | "grammatik";

export interface RechtschreibTreffer {
    /** UTF-16-Index im gesendeten Text (= JS-String-Index). */
    offset: number;
    laenge: number;
    art: RechtschreibArt;
    nachricht: string;
    vorschlaege: string[];
    regel: string;
}

/** Obergrenze des Backends je Prüfung. */
export const RECHTSCHREIB_MAX_ZEICHEN = 20_000;

/** Wie lange ein „nicht verfügbar" gilt, bevor erneut nachgefragt wird. */
const NICHT_VERFUEGBAR_MS = 5 * 60_000;

let statusAnfrage: Promise<boolean> | null = null;
let statusZeit = 0;

/**
 * Ist die Prüfung nutzbar? Einmal je Sitzung erfragt und geteilt; ein „nein"
 * wird nach fünf Minuten erneut geprüft. Fehler zählen als „nein" — der Editor
 * fällt dann still auf die Prüfung des Browsers zurück.
 */
export function rechtschreibStatus(): Promise<boolean> {
    const abgelaufen = Date.now() - statusZeit > NICHT_VERFUEGBAR_MS;
    if (statusAnfrage && !abgelaufen) return statusAnfrage;
    statusZeit = Date.now();
    statusAnfrage = apiFetch<{ aktiv?: boolean }>("rechtschreibung/status")
        .then((r) => r.data?.aktiv === true)
        .catch(() => false)
        .then((aktiv) => {
            // Ein „ja" gilt für die Sitzung; ein „nein" verfällt nach der Frist.
            if (aktiv) statusZeit = Number.POSITIVE_INFINITY;
            return aktiv;
        });
    return statusAnfrage;
}

/** Meldet einen Ausfall (z. B. 503 beim Prüfen): Status gilt erst einmal als „nein". */
export function rechtschreibAusfallMerken(): void {
    statusAnfrage = Promise.resolve(false);
    statusZeit = Date.now();
}

/** Nur für Tests: gemerkten Status vergessen. */
export function rechtschreibStatusZuruecksetzen(): void {
    statusAnfrage = null;
    statusZeit = 0;
}

export async function rechtschreibPruefen(text: string, signal?: AbortSignal): Promise<RechtschreibTreffer[]> {
    const { data } = await apiFetch<{ treffer?: RechtschreibTreffer[] }>("rechtschreibung/pruefen", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.slice(0, RECHTSCHREIB_MAX_ZEICHEN), sprache: "de-DE" }),
        signal,
    });
    return Array.isArray(data?.treffer) ? data.treffer : [];
}
